/* =====================================================================
   BIKES-REDESIGN.JS — Behaviors for the custom blog system
   - Scroll reveal (IntersectionObserver, blur+lift)
   - Hero loaded class (initial mount)
   - Reading progress bar (article)
   - Auto TOC + scroll-spy (article)
   - Mobile TOC accordion
   - Share buttons (copy link)
   - Magnetic button hover
   ===================================================================== */

(function () {
  'use strict';

  if (typeof window === 'undefined') return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Scroll reveal ---------- */
  function initReveal() {
    const els = document.querySelectorAll('.bk-reveal');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    // Activate the hide-then-reveal styles only now that JS+observer are usable.
    document.documentElement.classList.add('bk-reveal-ready');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    els.forEach((el, i) => {
      const delay = el.dataset.revealDelay || (i % 6) * 80;
      el.style.transitionDelay = delay + 'ms';
      io.observe(el);
    });
    // Safety net: if anything is still hidden after 3s (e.g. headless screenshot tools),
    // force everything visible so the page is never blank.
    setTimeout(() => {
      els.forEach((el) => el.classList.add('is-in'));
    }, 3000);
  }

  /* ---------- 2. Hero loaded (no-op, reserved for future video init) ---------- */
  function initHeroLoaded() {
    // Placeholder for future video autoplay init if needed.
  }

  /* ---------- 3. Reading progress bar ---------- */
  function initProgress() {
    const fill = document.querySelector('.bk-progress__fill');
    const article = document.querySelector('.bk-prose');
    if (!fill || !article) return;
    let ticking = false;
    function update() {
      const rect = article.getBoundingClientRect();
      const total = article.offsetHeight - window.innerHeight + rect.top + window.scrollY;
      const start = rect.top + window.scrollY - 80;
      const scrolled = window.scrollY - start;
      const pct = Math.max(0, Math.min(100, (scrolled / (total - start)) * 100));
      fill.style.width = pct + '%';
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /* ---------- 4. Auto-generate TOC + scroll spy ---------- */
  function initTOC() {
    const prose = document.querySelector('.bk-prose');
    const tocMount = document.querySelector('[data-bk-toc]');
    const tocMobileMount = document.querySelector('[data-bk-toc-mobile]');
    if (!prose || (!tocMount && !tocMobileMount)) return;

    const headings = prose.querySelectorAll('h2, h3');
    if (!headings.length) {
      // Hide TOC containers and remove grid offset
      const wrapper = document.querySelector('.bk-art-body.has-toc');
      if (wrapper) wrapper.classList.remove('has-toc');
      const tocEl = document.querySelector('.bk-toc');
      if (tocEl) tocEl.style.display = 'none';
      const tocM = document.querySelector('.bk-toc-mobile');
      if (tocM) tocM.style.display = 'none';
      return;
    }

    const items = [];
    headings.forEach((h, i) => {
      if (!h.id) {
        const slug = (h.textContent || '')
          .toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^\w\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-')
          .slice(0, 60) || 'section-' + i;
        h.id = slug + '-' + i;
      }
      items.push({ id: h.id, text: h.textContent || '', level: h.tagName.toLowerCase() });
    });

    function buildList(targetEl) {
      if (!targetEl) return;
      const ul = document.createElement('ul');
      ul.className = targetEl.dataset.bkTocMobile !== undefined ? '' : 'bk-toc__list';
      items.forEach((it) => {
        const li = document.createElement('li');
        li.className = 'bk-toc__item ' + (it.level === 'h3' ? 'is-h3' : 'is-h2');
        li.dataset.target = it.id;
        const a = document.createElement('a');
        a.href = '#' + it.id;
        a.textContent = it.text;
        li.appendChild(a);
        ul.appendChild(li);
      });
      targetEl.appendChild(ul);
    }

    buildList(tocMount);
    buildList(tocMobileMount);

    // Scroll spy
    const allTocItems = document.querySelectorAll('.bk-toc__item');
    if ('IntersectionObserver' in window && allTocItems.length) {
      const map = new Map();
      allTocItems.forEach((li) => {
        const id = li.dataset.target;
        if (!map.has(id)) map.set(id, []);
        map.get(id).push(li);
      });
      const spy = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              allTocItems.forEach((li) => li.classList.remove('is-active'));
              const lis = map.get(e.target.id);
              if (lis) lis.forEach((li) => li.classList.add('is-active'));
            }
          });
        },
        { rootMargin: '-20% 0px -65% 0px', threshold: 0 }
      );
      headings.forEach((h) => spy.observe(h));
    }

    // Mobile accordion toggle
    const mobile = document.querySelector('.bk-toc-mobile');
    const mobileBtn = document.querySelector('.bk-toc-mobile__btn');
    if (mobile && mobileBtn) {
      mobileBtn.addEventListener('click', () => mobile.classList.toggle('is-open'));
      // Close on link click
      mobile.querySelectorAll('a').forEach((a) =>
        a.addEventListener('click', () => mobile.classList.remove('is-open'))
      );
    }
  }

  /* ---------- 5. Share buttons (copy link) ---------- */
  function initShare() {
    const copyBtns = document.querySelectorAll('[data-bk-share="copy"]');
    copyBtns.forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        try {
          await navigator.clipboard.writeText(window.location.href);
          const orig = btn.getAttribute('aria-label') || 'Copy link';
          btn.setAttribute('aria-label', 'Link copiado');
          btn.classList.add('is-copied');
          setTimeout(() => {
            btn.setAttribute('aria-label', orig);
            btn.classList.remove('is-copied');
          }, 1800);
        } catch (err) {
          console.warn('clipboard failed', err);
        }
      });
    });
  }

  /* ---------- 6. Magnetic buttons (subtle pointer-attract on desktop) ---------- */
  function initMagnetic() {
    if (reduceMotion) return;
    if (window.matchMedia('(hover: none)').matches) return;
    const btns = document.querySelectorAll('.bk-btn[data-magnetic]');
    btns.forEach((btn) => {
      const strength = 12;
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * strength;
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * strength;
        btn.style.transform = `translate(${x}px, ${y - 2}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  /* ---------- 7. Content normalizer (respect editor, fix iframes + img sizing) ---------- */
  function normalizeProseContent() {
    var prose = document.querySelector('.bk-prose');
    if (!prose) return;

    // 1. Promote HTML width attr to inline style (so it beats CSS specificity)
    prose.querySelectorAll('img[width]').forEach(function (img) {
      if (!img.style.width) {
        img.style.width = img.getAttribute('width') + 'px';
        img.style.maxWidth = '100%';
      }
      if (!img.style.height && img.hasAttribute('height')) {
        img.style.height = 'auto';
      }
    });

    // 2. Remove empty paragraphs (no text, no media)
    prose.querySelectorAll('p').forEach(function (p) {
      if (!p.textContent.trim() && !p.querySelector('img, iframe, video')) {
        p.remove();
      }
    });

    // 3. Wrap bare iframes/videos in responsive container
    prose.querySelectorAll('iframe, video').forEach(function (media) {
      if (media.closest('.bk-video-wrap')) return;
      var wrap = document.createElement('div');
      wrap.className = 'bk-video-wrap';
      media.parentNode.insertBefore(wrap, media);
      wrap.appendChild(media);
      media.removeAttribute('width');
      media.removeAttribute('height');
    });
  }

  /* ---------- 9. Tag pills — set active based on URL ---------- */
  function initTagsActive() {
    const tags = document.querySelectorAll('.bk-tags .bk-tag');
    if (!tags.length) return;
    const url = new URL(window.location.href);
    const current = (url.searchParams.get('tag') || '').toLowerCase();
    tags.forEach((t) => {
      const tagVal = (t.dataset.tag || '').toLowerCase();
      if (!current && t.dataset.all === 'true') t.classList.add('is-active');
      else if (current && tagVal === current) t.classList.add('is-active');
    });
  }

  /* ---------- Boot ---------- */
  function boot() {
    initReveal();
    initHeroLoaded();
    initProgress();
    initTOC();
    initShare();
    initMagnetic();
    normalizeProseContent();
    initTagsActive();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
